import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Camera, Film, X } from "lucide-react";
import { api, mediaUrl, formatWIT, BRAND } from "@/lib/appApi";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";

function ytEmbed(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed${u.pathname}`;
    if (u.hostname.includes("youtube.com")) { const id = u.searchParams.get("v"); return id ? `https://www.youtube.com/embed/${id}` : url; }
    if (u.hostname.includes("vimeo.com")) return `https://player.vimeo.com/video${u.pathname}`;
  } catch {}
  return url;
}

export default function Galeri() {
  const { unitMap = {} } = useOutletContext() || {};
  const [galleries, setGalleries] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => { api.get("/public/galleries").then((r) => setGalleries(r.data)).catch(() => {}); }, []);

  const photos = galleries.filter((g) => g.type !== "video");
  const videos = galleries.filter((g) => g.type === "video");

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="font-serif text-4xl font-extrabold text-dlh-green mb-2">Galeri</h1>
      <p className="text-gray-600 mb-8">Dokumentasi kegiatan Dinas Lingkungan Hidup Kabupaten Mimika.</p>

      <Tabs defaultValue="foto">
        <TabsList className="mb-6">
          <TabsTrigger value="foto" data-testid="tab-foto"><Camera className="h-4 w-4 mr-2" />Foto</TabsTrigger>
          <TabsTrigger value="video" data-testid="tab-video"><Film className="h-4 w-4 mr-2" />Video</TabsTrigger>
        </TabsList>

        <TabsContent value="foto">
          {photos.length === 0 ? <Empty text="Belum ada album foto." /> : photos.map((g) => (
            <div key={g.id} className="mb-10">
              <AlbumHeader g={g} unitMap={unitMap} />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {(g.items || []).map((it, i) => (
                  <button key={i} onClick={() => setLightbox({ url: mediaUrl(it.url || it), caption: it.caption, credit: it.credit })}
                    className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group focus:ring-2 focus:ring-dlh-orange" data-testid={`gallery-photo-${g.id}-${i}`}>
                    <img src={mediaUrl(it.url || it)} alt={it.caption || g.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="video">
          {videos.length === 0 ? <Empty text="Belum ada video." /> : videos.map((g) => (
            <div key={g.id} className="mb-10">
              <AlbumHeader g={g} unitMap={unitMap} />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(g.items || []).map((it, i) => (
                  <div key={i} className="rounded-lg overflow-hidden border border-gray-200 bg-white">
                    <div className="aspect-video bg-black">
                      <iframe src={ytEmbed(it.url)} title={it.title || g.title} className="w-full h-full" allowFullScreen loading="lazy" />
                    </div>
                    {it.title && <p className="p-3 text-sm font-semibold text-gray-800">{it.title}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black border-none">
          {lightbox && (
            <div className="relative">
              <img src={lightbox.url} alt={lightbox.caption || ""} className="w-full max-h-[80vh] object-contain" />
              {(lightbox.caption || lightbox.credit) && (
                <div className="p-3 text-white text-sm bg-black/80">{lightbox.caption} {lightbox.credit && <span className="text-white/50">— {lightbox.credit}</span>}</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AlbumHeader({ g, unitMap }) {
  const unit = unitMap[g.unit];
  return (
    <div className="mb-3">
      <h2 className="font-serif text-xl font-bold text-dlh-green">{g.title}</h2>
      <p className="text-xs text-gray-500">{unit ? unit.short_name + " · " : ""}{g.event_date ? formatWIT(g.event_date) : ""}</p>
      {g.description && <p className="text-sm text-gray-600 mt-1">{g.description}</p>}
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-xl border border-dashed border-gray-300 p-12 text-center text-gray-500">{text}</div>;
}
